import * as React from 'react'
import * as Sentry from '@sentry/react'
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth'
import { useLocation, useNavigate } from 'react-router'
import { $path } from 'safe-routes'

import { recordDiagnostic } from '@modules/diagnostics/store'
import { getSearchQuery, useRouteUtils } from '@modules/route/routeUtils'
import { toast } from '@modules/shadcn/ui/Toast/use-toast'
import { safeDataLayer } from '@modules/utils/gtm'

import { clearAuthRejection, recordAuthRejection } from '@company/auth/authRejection'
import { authApp, firebaseLogout } from '@company/auth/firebase'
import { isLineLinkContinuationActive } from '@company/auth/lineOAuth'
import { classifyStaffMeFailure } from '@company/auth/staffMeFailure'
import { AppStateContainer } from '@company/context/AppStateContainer'
import { CompanyService } from '@company/gen_proto/api_proto/v1/company_service_pb'
import { type GetStaffAuthResponse } from '@company/gen_proto/api_proto/v1/staff_pb'
import { queryClient, useClient } from '@company/request/client'

// 同一タブ内で GA4 `login` event を 1 度だけ送るためのフラグ。
// useAuth は root.tsx と MenuList.tsx 両方で呼ばれるため、各 hook 内の
// useRef では別インスタンスとなり多重発火する。タブ単位で 1 回に制限したいので
// sessionStorage を使う (リロード越えはタブが残る限り保持され、タブを閉じれば消える)。
// logout 時にクリアして、同タブで再ログインしたら再発火するようにする。
const LOGIN_SENT_KEY = 'gtm:loginEventSent'
const hasSentLoginEvent = (): boolean => {
  if (typeof sessionStorage === 'undefined') return false
  try {
    return sessionStorage.getItem(LOGIN_SENT_KEY) === '1'
  } catch {
    return false
  }
}
const markLoginEventSent = (): void => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(LOGIN_SENT_KEY, '1')
  } catch {
    // private mode 等で書き込めない場合は諦める (アプリ機能には影響しない)
  }
}
const clearLoginEventSent = (): void => {
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.removeItem(LOGIN_SENT_KEY)
  } catch {
    // ignore
  }
}

export const useAuth = () => {
  const { updateStaff, updateAuthError } = AppStateContainer.useContainer()
  const [loading, setLoading] = React.useState(true)
  const navigate = useNavigate()
  const { getFullPath } = useRouteUtils()

  // -----------------------------------------------------------------------
  // 認証状態遷移を見るための ref 2 種類。State にしない理由:
  //   - onAuthStateChanged コールバックは React の render サイクル外で発火する
  //   - レンダリングのトリガーにする必要は無く、純粋に「直前の状態の覚書」が欲しいだけ
  //   - useState だとコールバック内で stale closure を踏むため、ref が素直
  //
  // wasAuthenticatedRef:
  //   「直前まで認証済みだったか」を表す。`true → true` は token refresh / 別タブ同期、
  //   `false → true` は新規ログイン or セッション復元、`true → false` はサインアウト。
  //   利用箇所:
  //     1. handleSignedOut でセッション切れトースト判定 (明示 logout でなければ通知)
  //     2. setUserInfo に渡す isNewLogin の判定材料 (GA4 `login` event 重複防止)
  //
  // isExplicitLogoutRef:
  //   「ユーザーが logout ボタンを押した」かを表す。`logout()` で true セット、
  //   handleSignedOut で消費。セッション切れ通知トーストを出すかどうかの分岐に使う。
  // -----------------------------------------------------------------------
  const wasAuthenticatedRef = React.useRef(false)
  const isExplicitLogoutRef = React.useRef(false)
  // 直前にサインインしていた Firebase uid。別アカウントでサインインし直したときに
  // react-query のキャッシュ（企業や uid をキーに含まないクエリ）を消すための比較用。
  const lastSignedInUidRef = React.useRef<string | null>(null)
  // getStaffMeが確定的な拒否で失敗し、こちらでサインアウトさせたことを表す。
  // handleSignedOutの汎用トースト（「セッションが切れました」）を抑止するために使う。
  // 拒否の具体的な理由はログイン画面のバナーで出すので、汎用トーストは邪魔になる。
  const suppressSessionExpiredToastRef = React.useRef(false)

  const client = useClient(CompanyService)

  const logout = async () => {
    isExplicitLogoutRef.current = true
    // 再試行画面から抜けられるようにする。root.tsxの分岐はauthError !== null && staff === null
    // だけを見ているので、ここで戻さないと /loginへ遷移してもログイン画面が描画されず、
    // 「ログイン画面に戻る」を押しても何も起きない（このPRが無くそうとしている症状そのもの）
    updateAuthError(null)
    // 明示ログアウト時に古い拒否理由が残っていたら捨てる（ログイン画面に無関係なバナーを出さない）
    clearAuthRejection()
    // 同タブで再ログインしたときに login event を再送できるようにフラグを落とす。
    clearLoginEventSent()
    try {
      // サーバー側でFirebaseのrefresh tokenを失効させる
      // 失敗してもクライアント側のサインアウトは必ず実行する
      await client.logout({})
    } catch (e) {
      console.error('サーバーログアウトに失敗しました', e)
    } finally {
      await firebaseLogout()
      // staffの状態をクリアしてからリダイレクト
      // クリアしないとuseUnAuthMiddlewareがstaffありと判断して/にリダイレクトしてしまう
      updateStaff(null)
      Sentry.setUser(null)
      // react-query のキャッシュを全消去する。ログアウトは SPA 内の遷移（フルリロードなし）なので、
      // 消さないと同じタブで別アカウントにログインし直したとき、企業や uid をキーに含まないクエリ
      // （プラン状態・会社情報・一覧など。staleTime 10 分）が前のアカウントのデータを返し続ける。
      // LINE ログインは OAuth のリダイレクトでページ全体が読み直されるため起きなかった。
      queryClient.clear()

      const currentPath = getFullPath()
      const path = currentPath ? $path('/login', { next: currentPath }) : $path('/login')
      navigate(path)
    }
  }

  /**
   * 認証後にアプリ側ステート (staff context / Sentry / GA4) を更新する。
   *
   * @param data getStaffMe の戻り値 (proto int64 由来の bigint フィールドを含む)
   * @param isNewLogin
   *   直前まで未認証だったか (= 今回の onAuthStateChanged で「初めて user が来た」か)。
   *   GA4 `login` event を **このセッションで何度も飛ばさない** ためのフラグ。
   *   `setDimension` / `clarity_send_user_id` は毎回再設定で問題ないが、
   *   `login` だけは「ログイン行為のカウント」なので true のときだけ送る (issue #1987)。
   */
  const setUserInfo = (data: GetStaffAuthResponse, isNewLogin: boolean) => {
    // GetStaffAuthResponseをGetStaffResponseに変換
    updateStaff(data)
    // sentry: 個人を特定できる氏名・メールアドレスは送らない（staff id から DB で引ける）
    Sentry.setUser({ id: data.id.toString(), company_id: data.companyId.toString() })
    // gtm
    // NOTE: data.id / data.companyId は proto int64 由来の bigint。
    // GTM の変数解決層が bigint を URL クエリにエンコードできずパラメータを無音でドロップするため、
    // dataLayer に push する前に必ず String() で文字列化する。
    // 参考: doc/coding_rules/all.md「BigInt / Protobuf シリアライズ規約」
    const gaStaffId = String(data.id)
    const gaCompanyId = String(data.companyId)
    // setDimension → GA4 user property (`company_id` / `staff_id`) を設定。
    // この push 以降の全イベントに user property が付与される。
    safeDataLayer({
      event: 'setDimension',
      gaStaffId,
      gaCompanyId,
    })
    // Clarity 側のセッションリプレイに user_id を紐付ける。
    safeDataLayer({
      event: 'clarity_send_user_id',
      gaStaffId,
      gaCompanyId,
    })
    if (isNewLogin && !hasSentLoginEvent()) {
      // 「ログインしたタイミング自体」を 1 回だけ計測 (issue #1987)。
      // 共通の `gaEvent` dispatcher Trigger に乗せ、UA 風の 4 軸
      // (event_category / event_action / event_label / event_value) を埋める。
      // GA4 側でこの 4 つはカスタムディメンションとして登録済み。
      // setDimension が先に走っているため、この event には company_id / staff_id が
      // user property として乗る。token refresh やセッション復元では飛ばさない。
      // useAuth が複数箇所 (root.tsx / MenuList.tsx) で呼ばれても重複しないよう
      // sessionStorage でタブ単位の冪等性を保証する (logout でクリア)。
      markLoginEventSent()
      safeDataLayer({
        event: 'gaEvent',
        gaCategory: 'auth',
        gaAction: 'login',
      })
    }
  }

  /**
   * getStaffMeの失敗を処理する。確定的な拒否と一時的な失敗で対応が逆になる
   * （@company/auth/staffMeFailure.ts）。
   *
   * 以前はここで `console.error` するだけだったため、Firebaseのサインインが通った後に
   * サーバが拒否すると、画面はログインフォームのまま何も起きなかった（2026-09の
   * 「ログインできないが何も出ない」事象）。サーバは停止済みスタッフ・契約なし企業に対して
   * ユーザー向けの日本語を返しているのに、それを捨てていた。
   */
  const handleStaffMeFailure = async (error: unknown) => {
    const failure = classifyStaffMeFailure(error)
    // 認証interceptorによる拒否はサーバのアクセスログに残らない（interceptorの順序が
    // tracing → auth → error → loggingで、認証がloggingの外側にあるため）。
    // フロントからSentryに送ることで初めて追えるようになる
    Sentry.captureException(error, {
      extra: { phase: 'getStaffMe', errorCode: failure.code, fatal: failure.fatal },
    })
    // RPC自体の失敗はtransportのinterceptorが記録済み。ここでは「確定的な拒否と判定して
    // サインアウトさせたか、一時的な失敗として再試行画面に回したか」を残す
    recordDiagnostic({
      kind: 'auth',
      label: failure.fatal ? 'getStaffMe.rejected' : 'getStaffMe.retryable',
      code: failure.code,
      message: failure.message,
    })

    if (!failure.fatal) {
      // 一時的な失敗。セッションは保ち、root.tsxの再試行画面に委ねる
      updateAuthError(failure.message)
      return
    }

    // 確定的な拒否。サインアウトしないとFirebaseセッションが残ったまま同じ失敗を繰り返し、
    // 画面が無反応のままになる。理由はログイン画面のバナーで出す
    // 預けるのはコードだけ。文言はログイン画面側で解決する（authRejection.tsの説明を参照）
    recordAuthRejection({ code: failure.code })
    suppressSessionExpiredToastRef.current = true
    updateAuthError(null)
    await firebaseLogout()
  }

  /**
   * スタッフ情報を取得してアプリ状態（staff context / Sentry / GA4）へ反映する。
   *
   * @param isNewLogin GA4の `login` eventを送るかの判定材料（setUserInfoの説明を参照）
   * @returns 取得に成功したか
   */
  const loadStaff = async (isNewLogin: boolean): Promise<boolean> => {
    try {
      const data = await client.getStaffMe({})
      // getStaffMe成功後にwasAuthenticatedRefを立てる。失敗した場合はfalseのままにして、
      // 次回のリトライ時にisNewLogin=trueとして再評価できるようにする
      // (PR #2056レビュー指摘)。
      wasAuthenticatedRef.current = true
      updateAuthError(null)
      setUserInfo(data, isNewLogin)
      return true
    } catch (error) {
      await handleStaffMeFailure(error)
      return false
    }
  }

  /**
   * 一時的な失敗のあとにスタッフ情報の取得をやり直す（再試行画面のボタンから呼ぶ）。
   * 既にサインアウトしている場合はログイン画面へ戻す。
   */
  const retryAuth = async () => {
    updateAuthError(null)
    if (authApp.currentUser === null) {
      navigate($path('/login'))
      return
    }
    await loadStaff(!wasAuthenticatedRef.current)
  }

  React.useEffect(() => {
    const handleSignedOut = () => {
      // 明示的な logout 以外の経路でセッションが切れた場合のみ通知
      // （別タブログアウト・refresh token失効・ユーザー無効化など）。
      // getStaffMeの確定的な拒否でこちらからサインアウトさせたときは、
      // 具体的な理由をログイン画面のバナーで出すので汎用トーストは出さない
      if (
        wasAuthenticatedRef.current &&
        !isExplicitLogoutRef.current &&
        !suppressSessionExpiredToastRef.current
      ) {
        toast({
          title: 'セッションが切れました',
          description: '再度ログインしてください',
          variant: 'destructive',
        })
      }
      wasAuthenticatedRef.current = false
      isExplicitLogoutRef.current = false
      suppressSessionExpiredToastRef.current = false
      updateStaff(null)
      Sentry.setUser(null)
      // セッション切れ等、logout() を通らないサインアウトでも前のアカウントのキャッシュを残さない
      queryClient.clear()
      lastSignedInUidRef.current = null
    }

    const cancelAuthListener = onAuthStateChanged(authApp, async user => {
      if (user) {
        // 「直前まで未認証だったか」を取得しておく (= isNewLogin)。
        // - 初回 / セッション復元 / 明示ログイン直後: false → 今回は新ログイン → GA4 login event 飛ばす
        // - token refresh / 別タブ同期: true → すでにログイン済 → login event 飛ばさない
        // 注意: signInEmailAndPassword 直後と sessionStorage 復元の区別はここでは取れないため、
        // 「タブ起動後に最初に user が来たタイミング = 新ログイン扱い」で許容している。
        const isNewLogin = !wasAuthenticatedRef.current
        // 別の uid でサインインし直した場合（サインアウトのイベントを経ずに切り替わる経路の保険）
        if (lastSignedInUidRef.current !== null && lastSignedInUidRef.current !== user.uid) {
          queryClient.clear()
        }
        lastSignedInUidRef.current = user.uid
        await loadStaff(isNewLogin)
      } else {
        handleSignedOut()
      }
      setLoading(false)
    })

    // refresh token 失敗時は onAuthStateChanged より先に user=null で発火するケースがあるため購読
    const cancelTokenListener = onIdTokenChanged(authApp, user => {
      if (!user) {
        handleSignedOut()
      }
    })

    return () => {
      cancelAuthListener()
      cancelTokenListener()
    }
  }, [])

  return { logout, loading, retryAuth }
}

export const useUnAuthMiddleware = () => {
  const location = useLocation()
  const { staff } = AppStateContainer.useContainer()
  const navigate = useNavigate()

  React.useEffect(() => {
    if (staff) {
      // ID/PW ログイン直後に LINE 連携へ continuation 中は自動リダイレクトを抑止する
      // （getStaffMe 成功で staff がセットされた瞬間に '/' へ飛ぶと、ホームが一瞬表示された後
      //  LINE へ遷移する UX 破綻が起きるため）。詳細: @company/auth/lineOAuth.ts
      if (isLineLinkContinuationActive()) return

      // testで何故か落ちるので、useRouteUtilsは使わない
      const search = getSearchQuery(location)
      const next = search.get('next')
      const nextPath = next ?? '/'
      navigate(nextPath)
    }
  }, [staff])
}
